import {Dispatch, SetStateAction, useState} from 'react'
import {getIndividualStakeholders} from '../modules/stakeholders/founders-and-promoters/core/_requests'
import {useAuth} from '../modules/auth'
import {getDropDownData} from '../modules/stakeholders/founders-and-promoters/core/_requests'
import {useLocation, useSearchParams} from 'react-router-dom'

export const useFetchIndividualStakeholders = (
  setIndividualStakeholders: Dispatch<SetStateAction<any>>,
  setImgName: Dispatch<SetStateAction<any>>,
  setCountryId: Dispatch<SetStateAction<any>>
) => {
  const [stakeHoldersDataLoading, setStakeHoldersDataLoading] = useState(false)
  const [openUserDropdown, setOpenUserDropdown] = useState(false)
  const [newEntryButtonClicked, setNewEntryButtonClicked] = useState(false)
  const [dropDownData, setDropDownData] = useState([])
  const [dropdownDataLoading, setDropDownDataLoading] = useState<boolean>(false)
  const {companyId} = useAuth()
  const [fieldClicked, setFieldClicked] = useState<string>('')
  const {pathname} = useLocation()
  const [searchParams] = useSearchParams()
  const tab = searchParams.get('tab')

  const fetchIndividualStakeholders = async (id: string, formik: any) => {
    if (!companyId) return
    setStakeHoldersDataLoading(true)
    try {
      const {
        data: {data, success},
      } = await getIndividualStakeholders(id, companyId)
      if (success) {
        setIndividualStakeholders(data)
        setImgName(data?.profileImage?.name)
        setCountryId(data?.country?.id)

        // Set form fields
        formik.setFieldValue('firstName', data?.firstName || '')
        formik.setFieldValue('lastName', data?.lastName || '')
        formik.setFieldValue('email', data?.email || '')
        formik.setFieldValue('profileImage', data?.profileImage?.id || '')
        formik.setFieldValue('phone', data?.phoneNumber || '')
        formik.setFieldValue('phoneCode', data?.phoneCode || '')
        formik.setFieldValue('gender', data?.gender || '')
        formik.setFieldValue('dateOfBirth', data?.dateOfBirth || '')
        formik.setFieldValue('address1', data?.address1 || '')
        formik.setFieldValue('address2', data?.address2 || '')
        formik.setFieldValue('country', data?.country || '')
        formik.setFieldValue('state', data?.state || '')
        formik.setFieldValue('city', data?.city || '')

        if (!(pathname.includes('add-stakeholder') && tab === 'institution')) {
          formik.setFieldValue('govtIssuedIdType', data?.govtIssuedIdType || '')
          formik.setFieldValue('govtIssuedIdNumber', data?.govtIssuedIdNumber || '')
        }

        if (data.skills) {
          formik.setFieldValue(
            'skills',
            data?.skills.split(',').map((skill: string) => ({
              label: skill,
              value: skill,
            }))
          )
        }

        formik.setFieldValue('zipCode', data?.zipCode || '')
        if (pathname.includes('add-board-member')) {
          formik.setFieldValue('workCompanyName', data?.workCompanyName || '')
          formik.setFieldValue('workIndustry', data?.workIndustry || '')
        }

        formik.setFieldValue('workExperience', data?.workExperience || '')
        formik.setFieldValue('education', data?.education || '')
        formik.setFieldValue('website', data?.website || '')
        formik.setFieldValue('nationality', data?.nationality || '')

        const errors = await formik.validateForm()
        console.log(errors, 'errors')

        // Clear all errors at once
        formik.setErrors({})

        // Optionally clear touched state for fields
        formik.setFieldTouched('firstName', false)
        formik.setFieldTouched('lastName', false)
        formik.setFieldTouched('email', false)
        formik.setFieldTouched('phone', false)
        formik.setFieldTouched('phoneCode', false)
        formik.setFieldTouched('workCompanyName', false)

        setStakeHoldersDataLoading(false)
      }
    } catch (error) {
      console.log(error)
    } finally {
      setStakeHoldersDataLoading(false)
    }
  }

  const resetFields = async (formik: any, sidebarFields: {[key in 1 | 2 | 3]: string[]}) => {
    const sidebar: 1 | 2 | 3 = formik.values.sidebar // Explicitly type sidebar

    // Clear only the fields related to the current sidebar
    const resetValues: any = {}
    sidebarFields[sidebar].forEach((field) => {
      resetValues[field] = formik.initialValues[field]
    })

    formik.setValues({
      ...formik.values,
      ...resetValues, // Apply the reset values
    })

    // Set touched fields to trigger validation for the current sidebar
    sidebarFields[sidebar].forEach((field) => {
      formik.setFieldTouched(field, true)
    })

    setImgName('') // Clear image if necessary
  }

  const onClickFocusText = (e: any, formik: any, stakeholderTeamDetails: any) => {
    setFieldClicked(e.target.ariaLabel)

    if (
      (e.target.ariaLabel === 'firstName' || e.target.ariaLabel === 'lastName') &&
      !stakeholderTeamDetails &&
      !newEntryButtonClicked
    ) {
      setOpenUserDropdown(true)
      if (e.target.ariaLabel === 'firstName') formik.setFieldTouched('firstName', false)
      if (e.target.ariaLabel === 'lastName') formik.setFieldTouched('lastName', false)

      if (dropDownData.length === 0) {
        fetchDropDownData(stakeholderTeamDetails)
      }
    }
  }

  const onClickBlurText = (e: any, formik: any) => {
    setFieldClicked(e.target.ariaLabel)

    if (e.target.ariaLabel === 'firstName') {
      formik.setFieldTouched('firstName', true)
    }
    if (e.target.ariaLabel === 'lastName') {
      formik.setFieldTouched('lastName', true)
    }
    if (e.target.ariaLabel === 'email') {
      formik.setFieldTouched('email', true)
    }
    if (e.target.ariaLabel === 'phone') {
      formik.setFieldTouched('phone', true)
    }
    if (e.target.ariaLabel === 'workCompanyName') {
      formik.setFieldTouched('workCompanyName', true)
    }
  }

  const fetchDropDownData = async (stakeholderTeamDetails: any) => {
    if (!companyId && !stakeholderTeamDetails) return
    try {
      setDropDownDataLoading(true)
      const {
        data: {data, success},
      } = await getDropDownData(companyId!)

      if (success) {
        setDropDownDataLoading(false)
        setDropDownData(data.individualList)
      }
    } catch (error) {
      setDropDownDataLoading(false)
      console.error('Error fetching dropdown data:', error)
    } finally {
      setDropDownDataLoading(false)
    }
  }

  return {
    fetchIndividualStakeholders,
    stakeHoldersDataLoading,
    resetFields,
    onClickFocusText,
    fieldClicked,
    onClickBlurText,
    openUserDropdown,
    dropdownDataLoading,
    setOpenUserDropdown,
    dropDownData,
    setDropDownData,
    setNewEntryButtonClicked,
  }
}
